/** FIX-011 / RE Stage 4 - the shapes the RE screens read from `/re/:tenantId/projects...` (stage-2/3 data, as the API returns it; token hashes are never part of it). */
export type ReProjectState =
  | "CREATED" | "SOURCE_READY" | "ANALYSIS_REQUESTED" | "ANALYSIS_AWAITING_CALLBACK" | "ANALYSIS_READY" | "ANALYSIS_TIMED_OUT" | "ANALYSIS_FAILED" | "ANALYSIS_APPROVED"
  | "PROPOSALS_REQUESTED" | "PROPOSALS_AWAITING_CALLBACK" | "PROPOSALS_READY" | "PROPOSALS_TIMED_OUT" | "PROPOSALS_FAILED" | "PROPOSAL_DRAFT" | "PROPOSAL_APPROVED" | "GENERATION_SUBMITTED";

export interface ReProjectDto {
  readonly id: string;
  readonly personaId: string;
  readonly sourceVideoId: string | null;
  readonly sourceKind: "upload" | "srr";
  readonly srrId: string | null;
  readonly state: ReProjectState;
  readonly currentAnalysisRevisionId: string | null;
  readonly activeRequestId: string | null;
  readonly copiedFromProjectId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ReAnalysisBody {
  readonly part_a: Record<string, unknown>;
  readonly part_b: Record<string, unknown>;
  readonly routine_report?: Record<string, unknown>;
}

export type ReRevisionStatus = "requested" | "awaiting_callback" | "ready" | "failed" | "timed_out" | "approved";

export interface ReRevisionDto {
  readonly id: string;
  readonly revisionNo: number;
  readonly status: ReRevisionStatus;
  readonly requestId: string | null;
  readonly deadlineAt: string | null;
  readonly usedAt: string | null;
  readonly userCorrectionText: string | null;
  readonly analysis: ReAnalysisBody | null;
  readonly resultSha256: string | null;
  readonly resultBytes: number | null;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
  readonly approvedAt: string | null;
  readonly createdAt: string;
}

export type ReProposalStatus = "proposed" | "draft" | "approved" | "superseded";
export interface ReProposalContent {
  readonly title?: string;
  readonly scene?: string;
  readonly outfit?: string;
  readonly environment?: string;
  readonly voice?: string;
  readonly music?: string;
  readonly effects?: string;
  readonly dialogue?: string | readonly { readonly speaker: string; readonly text: string }[];
  readonly [key: string]: unknown;
}
export interface ReProposalAdjustments {
  readonly scene?: string;
  readonly outfit?: string;
  readonly environment?: string;
}
export interface ReProposalDto {
  readonly id: string;
  readonly setId: string;
  readonly ordinal: number;
  readonly revisionNo: number;
  readonly status: ReProposalStatus;
  readonly content: ReProposalContent;
  readonly adjustments: ReProposalAdjustments;
  readonly selectedAt: string | null;
  readonly approvedAt: string | null;
}
export interface ReProposalSetDto {
  readonly id: string;
  readonly batchNo: number;
  readonly attemptNo: number;
  readonly status: "requested" | "awaiting_callback" | "ready" | "failed" | "timed_out";
  readonly deadlineAt: string | null;
  readonly createdAt: string;
  readonly errorCode: string | null;
}

/** Stage 6: the production request of an approved proposal version (one per version). */
export interface ReGenerationLinkDto {
  readonly id: string;
  readonly proposalId: string;
  readonly videoId: string;
  readonly jobId: string;
  readonly createdAt: string;
}

export interface ReProjectViewDto {
  readonly project: ReProjectDto;
  readonly generationLinks?: readonly ReGenerationLinkDto[];
  readonly analysisRevisions: readonly ReRevisionDto[];
  readonly proposalSets?: readonly ReProposalSetDto[];
  readonly proposals?: readonly ReProposalDto[];
}

/** Product limits (stage 5): proposals can be selected for production from any batch, with no upper limit (the cap of five was removed on 2026-10-18). Adjustable layers: scene, outfit, environment - nothing else. */
/** Stage 6: length of the produced clip (the production model's range, whole seconds). */
export const RE_GENERATION_MIN_SECONDS = 2;
export const RE_GENERATION_MAX_SECONDS = 30;
export const RE_ADJUSTABLE_FIELDS = ["scene", "outfit", "environment"] as const;

export interface ReProjectSummaryDto {
  readonly id: string;
  readonly personaId: string;
  readonly personaDisplayName: string | undefined;
  readonly sourceVideoId: string | null;
  readonly sourceKind: "upload" | "srr";
  readonly state: ReProjectState;
  readonly requestInFlight: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export const RE_STATE_LABEL: Readonly<Record<ReProjectState, string>> = {
  CREATED: "Created", SOURCE_READY: "Ready to analyze", ANALYSIS_REQUESTED: "Sending to Grok…", ANALYSIS_AWAITING_CALLBACK: "Grok is analyzing…", ANALYSIS_READY: "Analysis ready — review it",
  ANALYSIS_TIMED_OUT: "Analysis timed out", ANALYSIS_FAILED: "Analysis failed", ANALYSIS_APPROVED: "Analysis approved", PROPOSALS_REQUESTED: "Proposals requested", PROPOSALS_AWAITING_CALLBACK: "Proposals in progress",
  PROPOSALS_READY: "Proposals ready", PROPOSALS_TIMED_OUT: "Proposals timed out", PROPOSALS_FAILED: "Proposals failed", PROPOSAL_DRAFT: "Proposal selected", PROPOSAL_APPROVED: "Proposal approved", GENERATION_SUBMITTED: "Generation submitted",
};

/** States in which a Grok request is in flight (the page polls while one of them holds). */
export const RE_IN_FLIGHT_STATES: readonly ReProjectState[] = ["ANALYSIS_REQUESTED", "ANALYSIS_AWAITING_CALLBACK"];
export const RE_ANALYSIS_APPROVED_STATES: readonly ReProjectState[] = ["ANALYSIS_APPROVED", "PROPOSALS_REQUESTED", "PROPOSALS_AWAITING_CALLBACK", "PROPOSALS_READY", "PROPOSALS_TIMED_OUT", "PROPOSALS_FAILED", "PROPOSAL_DRAFT", "PROPOSAL_APPROVED", "GENERATION_SUBMITTED"];

/** Product decision (2026-10-17): the source video of an RE project is at most 30 seconds (the production model's limit). RE only; the server enforces the same rule. */
export const RE_MAX_SOURCE_SECONDS = 30;
/** Too long, or of unknown length (fails closed, like the server). */
export const reSourceTooLong = (durationSeconds: number | null | undefined): boolean => durationSeconds === null || durationSeconds === undefined || !(durationSeconds <= RE_MAX_SOURCE_SECONDS);
export const reSourceTooLongText = (durationSeconds: number | null | undefined): string =>
  durationSeconds === null || durationSeconds === undefined
    ? `This video's length is unknown. RE accepts source videos of at most ${RE_MAX_SOURCE_SECONDS} seconds.`
    : `This video is ${durationSeconds.toFixed(1)} s long. RE accepts source videos of at most ${RE_MAX_SOURCE_SECONDS} seconds.`;

/** Reads the duration of a local video file in the browser (metadata only). `null` when it cannot be read (the server checks again after the upload). */
export function readLocalVideoDuration(file: File, timeoutMs = 4000): Promise<number | null> {
  return new Promise((resolve) => {
    if (typeof document === "undefined" || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") { resolve(null); return; }
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    const done = (value: number | null): void => { window.clearTimeout(timer); video.removeAttribute("src"); URL.revokeObjectURL(url); resolve(value); };
    const timer = window.setTimeout(() => done(null), timeoutMs);
    video.preload = "metadata";
    video.onloadedmetadata = () => done(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => done(null);
    video.src = url;
  });
}

export const RE_ERROR_TEXT: Readonly<Record<string, string>> = {
  not_found: "That project, persona or source video was not found (or is not yours).",
  invalid_state: "That action is not available in the project's current state.",
  request_in_flight: "A request for this project is already in progress.",
  not_allowed: "That action is not allowed right now.",
  dispatch_failed: "Emma OS could not reach the analysis service. Nothing was analyzed; you can try again.",
  re_requests_not_configured: "RE is not configured on this deployment.",
  forbidden: "You do not have permission to do that in this workspace.",
  invalid_input: "The request was not valid.",
  plan_not_approved: "The plan can be downloaded only after at least one proposal has been approved.",
  result_integrity_failed: "The stored analysis did not pass its integrity check, so nothing was exported.",
  voice_not_ready: "This persona has no verified voice sample yet, so a proposal cannot be approved. Ask an administrator to assign a voice to the persona.",
  provider_rejected: "The video provider refused the request (for example, insufficient credits). Nothing was generated or charged; you can try again.",
  reuse_not_available: "This plan cannot be reused yet: its analysis was not approved.",
  re_generation_not_configured: "Video production is not configured on this deployment.",
  email_not_verified: "Verify your email address before creating videos.",
  source_too_long: `That source video is longer than ${RE_MAX_SOURCE_SECONDS} seconds (or its length is unknown). RE accepts source videos of at most ${RE_MAX_SOURCE_SECONDS} seconds.`,
};

/** Why a proposal action was refused (`not_allowed` + reason). */
export const RE_REASON_TEXT: Readonly<Record<string, string>> = {
  not_selected: "Select the proposal first.",
  superseded: "This proposal version was replaced by a newer one - the screen has been refreshed.",
  set_not_ready: "That proposal batch is not ready.",
  analysis_not_approved: "Approve the analysis first.",
  no_proposals: "There are no proposals to select yet.",
  no_initial_proposals: "More proposals can be requested only after the first proposals were produced.",
  not_approved: "Only a proposal that passed approval can be produced.",
  voice_changed: "The persona's voice changed after this proposal was approved. Approve the proposal again to use the new voice.",
  voice_sample_integrity: "The persona's stored voice sample failed its integrity check, so nothing was sent.",
  conflict: "The proposal changed at the same time; the screen has been refreshed.",
};

/** FIX-064 RE UI lock (owner t642, 2026-10-22): keys of the in-flight action tracking in `ReProject` — one per project-level action group and one per proposal. */
export const PROJECT_ACTION_KEY = "project";
export const MORE_PROPOSALS_ACTION_KEY = "more-proposals";
export const proposalActionKey = (proposalId: string): string => `proposal:${proposalId}`;
